export interface MainMenuConfig {
	menuID: string;
	toggles: string;
	styleClass: {
		menuOpen: string;
	};
	focusLock: {
		dialog: string;
		return: string;
		toggle: string;
	};
}

export class MainMenu {
	private readonly menu: HTMLElement;
	private readonly toggleOpen: HTMLElement;
	private readonly mouseOutHideDelay: number = 50;
	private focusFirstElement: HTMLElement;
	private focusLastElement: HTMLElement;
	private focusReturnElement: HTMLElement;

	constructor(private readonly config: MainMenuConfig) {
		this.menu = document.getElementById(config.menuID);
		// NOTE: toggle id syntax is backwards to preserve URL slugs
		this.toggleOpen = document.getElementById('menu-close');

		// Allows for non-js fallbacks
		this.menu.classList.add('js-ready');

		this.createButtons();
		this.setupEventListeners();

		// Toggles mobile menu based on if this.toggleOpen is on screen
		new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				// kinda gross, but there's just a lot of ternary
				let isDialog = entry.intersectionRatio > 0;
				// AXE devtools complains about aria-modal not being valid if "false"
				if (isDialog) {
					this.menu.setAttribute('aria-modal', 'true');
				} else {
					this.menu.removeAttribute('aria-modal');
				}
				this.menu.setAttribute('data-was', this.menu.getAttribute('role') ?? 'initial');
				this.menu.setAttribute('role', isDialog ? 'dialog' : '');
				this.menu.querySelectorAll('.menu-item-has-children > a[aria-expanded]').forEach( element => {
					element.setAttribute('aria-hidden', isDialog ? 'true' : 'false');
				});
				if (isDialog) {
					this.menu.addEventListener("transitionend", () => {
						this.menu.removeAttribute('data-was');
					}, {once: true});
				}
			});
		}).observe(this.toggleOpen);
	}

	private createButtonTemplate() {
		// Creates a button to be cloned later
		const icon = document.createElement('span');
		icon.classList.add('icon');
		icon.setAttribute('aria-hidden', 'true');

		const text = document.createElement('span');
		text.classList.add('text', 'sr-desktop');
		text.innerHTML = '<span class="sr-only">submenu</span>';

		const button = document.createElement('button');
		button.setAttribute('aria-expanded', 'false');
		button.append(text, icon);

		return button;
	}
	private createButtons(): void {
		const buttonTemplate = this.createButtonTemplate();

		function kebabcase(string: string) {
			return string
				.replace(/([a-z])([A-Z])/g, "$1-$2")
				.replace(/[\s_]+/g, '-')
				.toLowerCase();
		}

		this.menu.querySelectorAll('.menu-item-has-children').forEach(item => {
			const anchor = item.querySelector('A');

			// gets properties for aria attributes
			const name = anchor.textContent;
			const kebabcasename = kebabcase(name);
			const menuId = 'menu-' + kebabcasename;
			const labelId = 'label-' + kebabcasename;

			anchor.setAttribute('id', labelId);
			anchor.setAttribute('aria-expanded', 'false');

			const submenu = item.querySelector('.sub-menu');
			submenu.setAttribute('id', menuId);
			submenu.setAttribute('aria-labelledby', labelId);

			const button = buttonTemplate.cloneNode(true) as HTMLButtonElement;
			button.setAttribute('aria-controls', menuId);
			button.querySelector('.text').prepend(name + ' ');

			item.insertBefore(button, submenu);
		});
	}
	private setupEventListeners(): void {
		this.menu.querySelectorAll<HTMLElement>('.menu-item-has-children').forEach(item => {
			let mouseOutTimer: any;

			// Show the menu on mouse hover of the trigger, Ignore on mobile
			item.addEventListener('pointerenter', event => {
				if (this.menu.getAttribute('aria-modal') === 'true') return;

				this.submenuShow(event.target as Element);
				clearTimeout(mouseOutTimer);
			});

			/* Hide the menu when mouse hover leaves both the trigger and menu fly-out, but only after a short delay to help people jerky mouse movements (like those using head/eye trackers), Ignore on mobile */
			item.addEventListener('pointerleave', event => {
				if (this.menu.getAttribute('aria-modal') === 'true') return;

				mouseOutTimer = setTimeout(() => {
					this.submenuHide(event.target as Element);
				}, this.mouseOutHideDelay);
			});

			// Hide the menu when the user tabs out or escapes, Ignore tabs on mobile
			item.addEventListener('keydown', event => {
				let currentItem = item;
				if (event.key == 'Escape') {
					// allows for deep backing out
					if (item.querySelector('a').getAttribute("aria-expanded") == 'false') {
						currentItem = item.parentElement.closest('li');
						if (!currentItem) {
							return;
						}
					}
					event.stopPropagation();
					this.submenuHide(currentItem);
					currentItem.querySelector('button').focus();
				}

				if (this.menu.getAttribute('aria-modal') === 'true') return;

				const lastItem = currentItem.querySelector(":scope > .sub-menu > li:last-child a");
				const firstItem = currentItem.querySelector(":scope > .sub-menu > li:first-child a");
				if (
					(event.key === 'Tab' && !event.shiftKey && event.target === lastItem) ||
					(event.key === 'Tab' && event.shiftKey && event.target === firstItem)
				) {
					this.submenuHide(currentItem);
				}
			});

			// Toggle the menu when the trigger is activated
			item.querySelectorAll<HTMLButtonElement>(':scope > button').forEach(button => {
				button.addEventListener('click', event => {
					this.submenuToggle((event.target as Element).closest('li'));

					// Close other submenu if desktop
					if (this.menu.getAttribute('aria-modal') === 'true') return;

					this.menu.querySelectorAll('button[aria-expanded="true"]').forEach(button => {
						if (button === event.target) return;
						this.submenuHide(button.closest('li'));
					})
				});
			});
		});

		// Close the menu when the user activates something outside the navbar.
		document.body.addEventListener('click', event => {
			if (this.menu.contains(event.target as Element)) return;
			if (this.menu.getAttribute('aria-modal') === 'true' && this.toggleOpen == (event.target as Element).closest('a')) return;

			this.menu.querySelectorAll('button[aria-expanded="true"]').forEach(button => {
				this.submenuHide(button.closest('li'));
			});
		});

		// Open/close menu
		document.querySelectorAll(this.config.toggles).forEach(element => {
			element.addEventListener('click', event => {
				event.preventDefault();
				this.modalToggle();
			});
		});

	}

	private modalFocusLock(open: boolean) {
		const dialog = document.querySelector(this.config.focusLock.dialog);

		const focusLockEventHandler = (event: KeyboardEvent) => {
			if (event.target == this.focusFirstElement && event.key == 'Tab' && event.shiftKey) {
				event.preventDefault();
				this.focusLastElement.focus();
			} else if (event.target == this.focusLastElement && event.key == 'Tab' && !event.shiftKey) {
				event.preventDefault();
				this.focusFirstElement.focus();
			}
		};

		if (open) {
			this.focusFirstElement = document.querySelector(this.config.focusLock.toggle);
			this.focusLastElement = Array.from(dialog.querySelectorAll('a, button')).pop() as HTMLElement;

			this.menu.addEventListener('keydown', focusLockEventHandler);
			this.focusFirstElement.focus();
		} else {
			this.focusReturnElement = document.querySelector(this.config.focusLock.return);

			this.menu.removeEventListener('keydown', focusLockEventHandler);
			this.focusReturnElement.focus();
		}
	}

	private modalToggle(open?: boolean): void {
		if (open === undefined) {
			open = !document.body.classList.contains(this.config.styleClass.menuOpen);
		}
		// Menu Open class
		document.body.classList.toggle(this.config.styleClass.menuOpen);
		// Scroll locking
		document.querySelector('html').classList.toggle('lockScroll');
		// Toggle button focus: Relies on --close, --open naming convention
		(document.querySelector(this.config.toggles + (open ? '--close' : '--open')) as HTMLElement).focus();

		this.modalFocusLock(open);

		// Open current
		if (open) {
			this.menu.querySelectorAll('li[class*="current"]').forEach(element => {
				this.submenuShow(element);
			});
		}
	}

	private submenuHide(item: Element): void {
		item.querySelectorAll(':scope > a, :scope > button').forEach(element =>
			element.setAttribute('aria-expanded', 'false')
		);
	}
	private submenuShow(item: Element): void {
		item.querySelectorAll(':scope > a, :scope > button').forEach(element =>
			element.setAttribute('aria-expanded', 'true')
		);
	}
	private submenuToggle(item: Element): void {
		console.log(item.querySelector('a').getAttribute('aria-expanded'));
		if (item.querySelector('a').getAttribute('aria-expanded') === 'true') {
			this.submenuHide(item);
		} else {
			this.submenuShow(item);
		}
	}
}
